import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import type { Experiment } from '@/types/experiment';

interface StopExperimentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  experiment: Experiment | null;
  isStopping: boolean;
}

const StopExperimentDialog: React.FC<StopExperimentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  experiment,
  isStopping
}) => {
  if (!experiment) return null;

  // Calculate progress information
  const calculateProgress = () => {
        const totalTime = experiment.exp_time_total || 0;
        const elapsedTime = experiment.exp_time_counter || 0;
        const totalPoints = experiment.exp_points_total || 0;
        const completedPoints = experiment.exp_points_counter || 0;

    const timeProgress = totalTime > 0 ? Math.min((elapsedTime / totalTime) * 100, 100) : 0;
    const pointsProgress = totalPoints > 0 ? Math.min((completedPoints / totalPoints) * 100, 100) : 0;

    return {
      timeProgress: Math.round(timeProgress),
      pointsProgress: Math.round(pointsProgress),
      elapsedTime,
      totalTime,
      completedPoints,
      totalPoints
    };
  };

  const progress = calculateProgress();

  // Format time display
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getStatusDescription = (status: string): string => {
    switch (status) {
      case 'RUN':
        return 'running';
      case 'PAUSE':
        return 'paused';
      case 'RESUME':
        return 'running';
      case 'ASK':
        return 'waiting for user input';
      default:
        return status.toLowerCase();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={isStopping ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon color="error" />
        Stop {getStatusDescription(experiment.exp_status)} Experiment?
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            This will <strong>permanently stop</strong> the experiment and lose all progress.
          </Typography>
          <Typography variant="body2">
            The experiment cannot be resumed once stopped.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Current Progress:</strong>
          </Typography>
          
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Time Progress: {progress.timeProgress}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress.timeProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary">
              {formatTime(progress.elapsedTime)} / {formatTime(progress.totalTime)}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Measurements: {progress.pointsProgress}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress.pointsProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary">
              {progress.completedPoints} / {progress.totalPoints} measurements
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          <strong>Experiment ID:</strong> {experiment.exp_id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Test ID:</strong> {experiment.test_id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Profile ID:</strong> {experiment.profile_id}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button 
          onClick={onClose} 
          disabled={isStopping}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          disabled={isStopping}
          variant="contained" 
          color="error"
          startIcon={isStopping ? <CircularProgress size={16} /> : undefined}
        >
          {isStopping ? 'Stopping...' : 'Stop Experiment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StopExperimentDialog; 